/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC4,
	ResumeIndicatorC4Data,
} from "@/services/esus/indicator/esf/c4/getResumeIndicatorC4";
import {
	getResumeIndicatorC4ByHealthTeam,
	ResumeIndicatorC4ByHealthTeamData,
} from "@/services/esus/indicator/esf/c4/getResumeIndicatorC4ByHealthTeam";
import {
	DetailedCitizenScoresIndicatorC4Data,
	getDetailedCitizensScoresIndicatorC4,
} from "@/services/esus/indicator/esf/c4/getDetailedCitizensScoresIndicatorC4";
import {
	DetailedCitizenAcsVisitsIndicatorC4Data,
	getDetailedCitizensAcsVisitsIndicatorC4,
} from "@/services/esus/indicator/esf/c4/getDetailedCitizensAcsVisitsIndicatorC4";
import {
	DetailedCitizenAttendancesIndicatorC4Data,
	getDetailedCitizensAttendancesIndicatorC4,
} from "@/services/esus/indicator/esf/c4/getDetailedCitizensAttendancesIndicatorC4";

import { IndicatorEsfC4Resume } from "./IndicatorEsfC4Resume";
import { IndicatorEsfC4CitizensAttendances } from "./IndicatorEsfC4CitizensAttendances";
import { IndicatorEsfC4HealthTeams } from "./IndicatorEsfC4HealthTeams";
import { IndicatorEsfC4CitizensScores } from "./IndicatorEsfC4CitizensScores";
import { IndicatorEsfC4CitizensAcsVisits } from "./IndicatorEsfC4CitizensAcsVisits";

type IndicatorEsfC4Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC4({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC4Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC4, setResumeIndicatorC4] = useState<ResumeIndicatorC4Data>(
		new ResumeIndicatorC4Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC4ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizensScores, setPageNumberCitizensScores] = useState(1);
	const [searchCitizensScore, setSearchCitizensScore] = useState("");
	const [isEvaluatedCitizensScore, setIsEvaluatedCitizensScore] = useState<boolean>();
	const debouncedSearchCitizensScore = useDebounce(searchCitizensScore, 2000);
	const [paginatedCitizensScores, setPaginatedCitizensScores] = useState(
		new Paginated<DetailedCitizenScoresIndicatorC4Data>()
	);
	const [isLoadingCitizensScores, setIsLoadingCitizensScores] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC4Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensAcsVisits, setPageNumberCitizensAcsVisits] = useState(1);
	const [searchCitizensAcsVisit, setSearchCitizensAcsVisit] = useState("");
	const [isEvaluatedCitizensAcsVisit, setIsEvaluatedCitizensAcsVisit] = useState<boolean>();
	const debouncedSearchCitizensAcsVisit = useDebounce(searchCitizensAcsVisit, 2000);
	const [paginatedCitizensAcsVisits, setPaginatedCitizensAcsVisits] = useState(
		new Paginated<DetailedCitizenAcsVisitsIndicatorC4Data>()
	);
	const [isLoadingCitizensAcsVisits, setIsLoadingCitizensAcsVisits] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC4(),
			handleGetResumeIndicatorC4ByHealthTeam(),
			handleGetDetailedCitizensScoresIndicatorC4(),
			handleGetDetailedCitizensWithAttendancesIndicatorC4(),
			handleGetDetailedCitizensAcsVisitsIndicatorC4(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC4() {
		setResumeIndicatorC4(
			await getResumeIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC4ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC4ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC4ByHealthTeam() {
		return (
			await getResumeIndicatorC4ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensScoresIndicatorC4(pageNumber?: number) {
		setIsLoadingCitizensScores(true);
		setPaginatedCitizensScores(
			await getDetailedCitizensScoresIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensScores(false);
	}

	async function handleGetAllDetailedCitizensScoresIndicatorC4() {
		return (
			await getDetailedCitizensScoresIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC4(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC4() {
		return (
			await getDetailedCitizensAttendancesIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensAcsVisitsIndicatorC4(pageNumber?: number) {
		setIsLoadingCitizensAcsVisits(true);
		setPaginatedCitizensAcsVisits(
			await getDetailedCitizensAcsVisitsIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensAcsVisits(false);
	}

	async function handleGetAllDetailedCitizensAcsVisitsIndicatorC4() {
		return (
			await getDetailedCitizensAcsVisitsIndicatorC4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC4ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC4ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensScoresIndicatorC4(pageNumberCitizensScores);
	}, [pageNumberCitizensScores]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensScores === 1) {
				handleGetDetailedCitizensScoresIndicatorC4(pageNumberCitizensScores);
			} else {
				setPageNumberCitizensScores(1);
			}
		}
	}, [debouncedSearchCitizensScore, isEvaluatedCitizensScore]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC4(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC4(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensAcsVisitsIndicatorC4(pageNumberCitizensAcsVisits);
	}, [pageNumberCitizensAcsVisits]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensAcsVisits === 1) {
				handleGetDetailedCitizensAcsVisitsIndicatorC4(pageNumberCitizensAcsVisits);
			} else {
				setPageNumberCitizensAcsVisits(1);
			}
		}
	}, [debouncedSearchCitizensAcsVisit, isEvaluatedCitizensAcsVisit]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C4 - Cuidado da Pessoa com Diabetes");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC4Resume resume={resumeIndicatorC4} />
			<IndicatorEsfC4HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC4ByHealthTeam={
					handleGetAllResumeIndicatorC4ByHealthTeam
				}
			/>
			<IndicatorEsfC4CitizensScores
				paginatedCitizensScores={paginatedCitizensScores}
				isLoading={isLoadingCitizensScores}
				search={searchCitizensScore}
				setSearch={setSearchCitizensScore}
				isEvaluated={isEvaluatedCitizensScore}
				setIsEvaluated={setIsEvaluatedCitizensScore}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensScores}
				setPageNumber={setPageNumberCitizensScores}
				handleGetAllDetailedCitizensScoresIndicatorC4={
					handleGetAllDetailedCitizensScoresIndicatorC4
				}
			/>
			<IndicatorEsfC4CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC4={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC4
				}
			/>
			<IndicatorEsfC4CitizensAcsVisits
				paginatedCitizensAcsVisits={paginatedCitizensAcsVisits}
				isLoading={isLoadingCitizensAcsVisits}
				search={searchCitizensAcsVisit}
				setSearch={setSearchCitizensAcsVisit}
				isEvaluated={isEvaluatedCitizensAcsVisit}
				setIsEvaluated={setIsEvaluatedCitizensAcsVisit}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensAcsVisits}
				setPageNumber={setPageNumberCitizensAcsVisits}
				handleGetAllDetailedCitizensAcsVisitsIndicatorC4={
					handleGetAllDetailedCitizensAcsVisitsIndicatorC4
				}
			/>
		</>
	);
}
